import React from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import { CalendarToday, AccessTime } from '@mui/icons-material';
import LoadingBackdrop from "../Common/LoadingBackdrop";
import { PER_PAGE_OPTIONS } from "../../constants/pagination";

const RequestsTable = ({
  data = [],
  searchText = "",
  pagination = { total: 0, page: 1, per_page: 10, total_pages: 0 },
  onPageChange,
  onRowsPerPageChange
}) => {
  const loading = useSelector((state) => state.requests.isLoading);

  // Client-side search filter (server doesn't support search)
  const filteredRequests = searchText
    ? data.filter(request =>
        request.request_message?.toLowerCase().includes(searchText.toLowerCase()) ||
        request.user_email?.toLowerCase().includes(searchText.toLowerCase()) ||
        request.user_full_name?.toLowerCase().includes(searchText.toLowerCase())
      )
    : data;

  if (loading) {
    return <LoadingBackdrop open={loading} />;
  }

  return (
    <Box sx={{ py: 2 }}>
      <TableContainer
        component={Paper}
        sx={{
          marginTop: 3,
          boxShadow: 2,
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell width="120px"><strong>Request Ref. #</strong></TableCell>
              <TableCell><strong>Message</strong></TableCell>
              <TableCell width="220px"><strong>User</strong></TableCell>
              <TableCell width="140px"><strong>Date & Time</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!filteredRequests || filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {searchText ? "No requests found matching your search" : "No requests available"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests.map((request) => {
                const epoch = request.timestamp || request.request_epoch;
                const dt = new Date(epoch * 1000);

                return (
                  <React.Fragment key={request.id || request.request_id}>
                    <TableRow
                      sx={{
                        '&:hover': { backgroundColor: '#f5f5f5' }
                      }}
                    >
                      <TableCell width="120px">{request.reference_number || request.request_reference_number}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          {request.request_message}
                        </Typography>
                      </TableCell>
                      <TableCell width="220px">
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {request.user_full_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {request.user_email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell width="140px">
                        <Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <CalendarToday sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {dt.toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <AccessTime sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {dt.toLocaleTimeString()}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={onPageChange}
          rowsPerPage={pagination.per_page}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={PER_PAGE_OPTIONS}
          labelRowsPerPage="Page Size:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
          showFirstButton
          showLastButton
        />
      </TableContainer>
    </Box>
  );
};

export default RequestsTable;